import React, { useEffect, useState } from "react";
import { Search, BookOpen, Clock, ChevronDown, Paperclip, Eye, Download as DownloadIcon, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { PageHeader, EmptyState, TypeBadge, SectionLabel } from "@/components/Common";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";

function humanSize(n) {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${i === 0 ? n.toFixed(0) : n.toFixed(1)} ${u[i]}`;
}

function attachmentUrl(docId, aid, { inline = true } = {}) {
  const tok = localStorage.getItem("kp_token") || "";
  const q = new URLSearchParams({ auth: tok, inline: inline ? "true" : "false" });
  return `${API}/documents/${docId}/attachments/${aid}/download?${q.toString()}`;
}

export default function Knowledge({ kind, title }) {
  const { activeProgram } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/knowledge", { params: { program: activeProgram } }).then((r) => setItems(r.data)).catch(() => {});
  }, [activeProgram]);

  const base = kind === "sop" ? items.filter((x) => x.type === "sop") : items.filter((x) => x.type !== "sop");
  const types = [...new Set(base.map((x) => x.type))];
  const filtered = base.filter((x) => {
    if (filter !== "all" && x.type !== filter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return x.title.toLowerCase().includes(t) || x.content.toLowerCase().includes(t) || (x.tags || []).join(" ").toLowerCase().includes(t);
  });

  return (
    <div>
      <PageHeader
        eyebrow={kind === "sop" ? "The Library · Section A" : "The Library · Section B"}
        title={title}
        subtitle={kind === "sop"
          ? "Standard operating procedures, curated for your program and role."
          : "FAQs, workflows, business rules and troubleshooting guides — the reference desk."}
        testid="knowledge-header">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search within this section…"
            data-testid="knowledge-search" className="pl-10 h-11 rounded-full bg-card" />
        </div>
      </PageHeader>

      {kind !== "sop" && types.length > 1 && (
        <div className="mb-6">
          <SectionLabel>Filter by type</SectionLabel>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFilter("all")} data-testid="sop-filter-all"
              className={`text-xs font-medium rounded-full px-3.5 py-1.5 border transition-all ${
                filter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/70 border-border hover:border-primary/40"}`}>
              All ({base.length})
            </button>
            {types.map((t) => {
              const label = t === "faq" ? "FAQ" : t.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
              return (
                <button key={t} onClick={() => setFilter(t)} data-testid={`sop-filter-${t}`}
                  className={`text-xs font-medium rounded-full px-3.5 py-1.5 border transition-all ${
                    filter === t ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/70 border-border hover:border-primary/40"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-3">
        {filtered.length} article{filtered.length === 1 ? "" : "s"}
      </p>

      {filtered.length === 0 ? (
        <EmptyState icon={BookOpen} title="Nothing here yet" hint="Try a different search or filter — or ask the assistant instead." />
      ) : (
        <Accordion type="single" collapsible className="space-y-3 stagger" data-testid="knowledge-list">
          {filtered.map((item) => (
            <AccordionItem key={item.id} value={item.id}
              className="rounded-2xl border border-border bg-card px-5 md:px-6 data-[state=open]:border-primary/40 data-[state=open]:shadow-[0_10px_30px_-16px_hsl(224_54%_21%/0.2)] transition-all overflow-hidden">
              <AccordionTrigger className="hover:no-underline py-5 [&>svg]:hidden group">
                <div className="flex items-start gap-4 text-left flex-1 min-w-0">
                  <TypeBadge type={item.type} />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-lg text-foreground leading-tight">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span className="font-mono">{item.source}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {item.last_updated}</span>
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="border-t border-border pt-4 pb-2">
                  <div className="text-[15px] text-foreground/85 whitespace-pre-line leading-[1.75] font-editorial">
                    {item.content}
                  </div>

                  {item.attachments?.length > 0 && (
                    <div className="mt-5">
                      <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Paperclip className="w-3 h-3" /> Attached PDFs
                      </p>
                      <div className="space-y-2" data-testid={`article-attachments-${item.id}`}>
                        {item.attachments.map((a) => (
                          <div key={a.id} className="rounded-lg border border-border bg-background/60 p-3 flex items-center gap-3" data-testid={`article-attachment-${a.id}`}>
                            <div className="w-9 h-9 rounded-lg bg-secondary/60 flex items-center justify-center shrink-0">
                              <FileText className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-foreground truncate">{a.title || a.filename}</p>
                              <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{a.filename} {a.size ? `· ${humanSize(a.size)}` : ""}</p>
                            </div>
                            <a href={attachmentUrl(item.id, a.id, { inline: true })} target="_blank" rel="noreferrer"
                               data-testid={`article-attachment-view-${a.id}`}
                               className="text-xs font-semibold text-primary flex items-center gap-1 kp-link">
                              <Eye className="w-3.5 h-3.5" /> View
                            </a>
                            <a href={attachmentUrl(item.id, a.id, { inline: false })}
                               data-testid={`article-attachment-download-${a.id}`}
                               className="text-xs font-semibold rounded-full bg-primary text-primary-foreground px-3 py-1.5 flex items-center gap-1 hover:opacity-95 transition">
                              <DownloadIcon className="w-3.5 h-3.5" /> Download
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-5 pt-4 border-t border-border/60">
                      {item.tags.map((t) => (
                        <span key={t} className="text-[11px] font-mono uppercase tracking-wider rounded-full bg-secondary/50 text-primary px-2.5 py-1">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
